// src/CesiumLayer.js
import React, { useEffect, useRef, useState } from "react";
import { CESIUM_ION_TOKEN } from "./token";
import OrthoCesiumLayer from "./OrthoCesiumLayer";
import NdviCesiumLayer from "./NdviCesiumLayer";
import ThreeDCesiumLayer from "./ThreeDCesiumLayer";
import VectorCesium from "./VectorCesium";
import CrossSectionCesium from "./CrossSectionCesium";
import SurveyProgressOverlay from "./SurveyProgressOverlay";
import ImageCesium from "./ImageCesium"; 
import LidarCesium from "./LidarCesium"; 

const DEFAULT_API_BASE = "";
const STEPS = ["Requested", "Surveyed", "Processed", "Uploaded", "Verified"];

// --- 1. NEW COMPASS WIDGET COMPONENT ---
// Separating this ensures the listener attaches cleanly to the viewer instance
const CompassWidget = ({ viewer }) => {
  const compassRef = useRef(null);

  useEffect(() => {
    if (!viewer || !compassRef.current) return;

    const Cesium = window.Cesium;
    
    // Function to update rotation based on camera heading
    const updateRotation = () => {
        if (viewer.isDestroyed() || !compassRef.current) return;
        
        // viewer.camera.heading is in Radians (Clockwise from North)
        // We convert to degrees. 
        // If camera looks East (90°), Compass needle (North) must rotate -90° (Left)
        const heading = Cesium.Math.toDegrees(viewer.camera.heading);
        compassRef.current.style.transform = `rotate(-${heading}deg)`;
    };

    // 1. Attach to the Render Loop (fires every frame)
    viewer.scene.postRender.addEventListener(updateRotation);
    
    // 2. Run once immediately to set initial position
    updateRotation();

    // 3. Cleanup listener on unmount
    return () => {
      if (viewer && !viewer.isDestroyed()) {
        viewer.scene.postRender.removeEventListener(updateRotation);
      }
    };
  }, [viewer]); // Re-run only if viewer instance changes

  const resetNorth = () => {
    if (!viewer) return;
    const currentPitch = viewer.camera.pitch;
    const currentPos = viewer.camera.position;
    
    viewer.camera.flyTo({
      destination: currentPos,
      orientation: {
        heading: 0.0, // North
        pitch: currentPitch,
        roll: 0.0
      },
      duration: 1.0
    });
  };

  return (
    <div style={compassContainerStyle}>
        <div 
            ref={compassRef} 
            onClick={resetNorth}
            title="Click to reset North"
            style={compassInnerStyle}
        >
            {/* SVG Compass Icon */}
            <svg width="45" height="45" viewBox="0 0 100 100" style={{ display: 'block', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))' }}>
                <circle cx="50" cy="50" r="45" fill="rgba(255,255,255,0.95)" stroke="#94a3b8" strokeWidth="2" />
                <path d="M50 15 L62 50 L50 85 L38 50 Z" fill="#e2e8f0" /> {/* Grey Bottom (South) */}
                <path d="M50 15 L62 50 L50 50 L38 50 Z" fill="#ef4444" /> {/* Red Top (North) */}
                <text x="50" y="12" textAnchor="middle" fontSize="14" fill="#ef4444" fontWeight="800" style={{ pointerEvents:'none'}}>N</text>
            </svg>
        </div>
        <div style={compassHelpTextStyle}>
             Press <b>N</b> for North<br/>
             Press <b>U</b> for Top-Down
        </div>
    </div>
  );
};


// --- 2. MAIN CESIUM LAYER ---
export default function CesiumLayer({ 
  visible, 
  onClose, 
  latestSurvey, 
  apiBase,
  id, 
  surveyHistory,
  onSurveyUpdated
}) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const overlayRef = useRef(null);

  const [isViewerReady, setIsViewerReady] = useState(false);
  const [statusText, setStatusText] = useState("Initializing...");

  // --- Layer Visibility State ---
  const [showOrtho, setShowOrtho] = useState(true);
  const [showNdvi, setShowNdvi] = useState(true);
  const [show3D, setShow3D] = useState(true);
  const [showVectors, setShowVectors] = useState(true);
  const [showImages, setShowImages] = useState(true);
  const [showLidar, setShowLidar] = useState(true); 

  // --- Tool State ---
  const [isCrossSectionMode, setIsCrossSectionMode] = useState(false);

  const finalApiBase = apiBase || DEFAULT_API_BASE;

  // Active Step Logic
  const activeStepIndex = (() => {
    const st = (latestSurvey?.status || "Requested").toString();
    const idx = STEPS.findIndex((s) => s.toLowerCase() === st.toLowerCase());
    if (idx === -1) return 0;
    return idx;
  })();

  const currentLocationName = 
    latestSurvey?.location_name || 
    latestSurvey?.name || 
    latestSurvey?.project_name || 
    id;

  // --- Keyboard Shortcuts (N / U) ---
  useEffect(() => {
    if (!isViewerReady || !viewerRef.current) return;
    const viewer = viewerRef.current;
    const Cesium = window.Cesium;

    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();
      
      // 'n' -> Reset Heading to North
      if (key === 'n') {
        viewer.camera.flyTo({
          destination: viewer.camera.position,
          orientation: {
            heading: 0.0,
            pitch: viewer.camera.pitch,
            roll: 0.0
          },
          duration: 1.0
        });
      }

      // 'u' -> Top Down View
      if (key === 'u') {
        viewer.camera.flyTo({
          destination: viewer.camera.position,
          orientation: {
            heading: viewer.camera.heading,
            pitch: Cesium.Math.toRadians(-90),
            roll: 0.0
          },
          duration: 1.0
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isViewerReady]);


  // --- Cesium Init ---
  useEffect(() => {
    if (!visible || !containerRef.current) return;
    if (viewerRef.current) return;

    const Cesium = window.Cesium;
    if (!Cesium) {
      setStatusText("Error: Cesium not loaded");
      return;
    }

    Cesium.Ion.defaultAccessToken = CESIUM_ION_TOKEN;
    setStatusText("Loading Globe...");

    try {
      const viewer = new Cesium.Viewer(containerRef.current, {
        imageryProvider: false,
        baseLayerPicker: false,
        terrainProvider: new Cesium.EllipsoidTerrainProvider(),
        animation: false,
        timeline: false,
        geocoder: false,
        homeButton: false,
        sceneModePicker: true,
        navigationHelpButton: false,
        fullscreenButton: false,
        selectionIndicator: false,
        infoBox: false,
      });

      const esriProvider = new Cesium.UrlTemplateImageryProvider({
        url: "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        credit: new Cesium.Credit("Esri, Maxar"),
        maximumLevel: 19,
      });
      viewer.imageryLayers.addImageryProvider(esriProvider);

      viewer.scene.globe.enableLighting = false;
      viewer.scene.globe.depthTestAgainstTerrain = false;
      viewer.scene.globe.baseColor = Cesium.Color.GRAY;

      viewerRef.current = viewer;
      setIsViewerReady(true);
      setStatusText("Ready");

    } catch (error) {
      console.error("Cesium Init Error:", error);
      setStatusText("Error initializing map");
    }

    return () => {
      if (viewerRef.current && !viewerRef.current.isDestroyed()) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
      setIsViewerReady(false);
    };
  }, [visible]);

  if (!visible) return null;

  const TOP_OFFSET = 170; 

  return (
    <div style={{ position: "fixed", top: 0, left: 280, right: 0, bottom: 0, zIndex: 2000, backgroundColor: "#000" }}>
      
      {/* --- HEADER --- */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 280, zIndex: 2005 }}>
        <SurveyProgressOverlay
            title={`Survey Progress: ${id ?? "—"}`}
            activeStep={activeStepIndex}
            steps={STEPS}
            surveyTime={latestSurvey?.survey_time ?? latestSurvey?.survey_date ?? null}
            overlayRef={overlayRef}
            history={surveyHistory || []}
            latestSurvey={latestSurvey}
            locationName={currentLocationName}
            apiBase={finalApiBase} 
            onSurveyUpdated={(newSurvey) => {
                if(onSurveyUpdated) onSurveyUpdated(newSurvey);
            }}
        />
      </div>

      {/* --- LEFT BUTTONS --- */}
      <div style={{ ...mapControlStyle, top: TOP_OFFSET, left: 12 }}>
          <button onClick={onClose} style={textButtonStyle}>Exit 3D View</button>
          <div style={dividerStyle} />
          <button 
            onClick={() => setIsCrossSectionMode(!isCrossSectionMode)} 
            style={{
                ...textButtonStyle,
                color: isCrossSectionMode ? '#2563eb' : '#333',
                fontWeight: isCrossSectionMode ? '700' : '500'
            }}
          >
            {isCrossSectionMode ? "Stop Measuring" : "📐 Cross Section"}
          </button>
      </div>

      {/* --- RIGHT LAYERS --- */}
      <div 
        style={{ ...mapControlStyle, top: TOP_OFFSET, right: 12, flexDirection: 'column', alignItems: 'flex-start', gap: 6, padding: "8px 12px" }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: "#0f172a", marginBottom: 2 }}>Visible Layers</div>
        <label style={layerRowStyle}>
          <input type="checkbox" checked={show3D} onChange={(e) => setShow3D(e.target.checked)} /> <span>3D Terrain</span>
        </label>
        <label style={layerRowStyle}>
          <input type="checkbox" checked={showOrtho} onChange={(e) => setShowOrtho(e.target.checked)} /> <span>Ortho Map</span>
        </label>
        <label style={layerRowStyle}>
          <input type="checkbox" checked={showLidar} onChange={(e) => setShowLidar(e.target.checked)} /> <span>LiDAR Point Cloud</span>
        </label>
        <label style={layerRowStyle}>
          <input type="checkbox" checked={showNdvi} onChange={(e) => setShowNdvi(e.target.checked)} /> <span>NDVI</span>
        </label>
        <label style={layerRowStyle}>
          <input type="checkbox" checked={showVectors} onChange={(e) => setShowVectors(e.target.checked)} /> <span>Vectors (GeoJSON)</span>
        </label>
        <label style={layerRowStyle}>
          <input type="checkbox" checked={showImages} onChange={(e) => setShowImages(e.target.checked)} /> <span>Ground Images</span>
        </label>
        <div style={{ fontSize: 10, color: "#888", marginTop: 4, alignSelf: 'flex-end' }}>{statusText}</div>
      </div>

      {/* --- COMPASS WIDGET (Bottom Right) --- */}
      {/* We only render this when viewer is ready to avoid null refs */}
      {isViewerReady && viewerRef.current && (
         <CompassWidget viewer={viewerRef.current} />
      )}

      {/* --- CESIUM CONTAINER --- */}
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />

      {/* --- DATA LAYERS --- */}
      {isViewerReady && viewerRef.current && (
        <>
          {show3D && <ThreeDCesiumLayer viewer={viewerRef.current} latestSurvey={latestSurvey} apiBase={finalApiBase} />}
          {showOrtho && <OrthoCesiumLayer viewer={viewerRef.current} latestSurvey={latestSurvey} apiBase={finalApiBase} />}
          {showLidar && <LidarCesium viewer={viewerRef.current} latestSurvey={latestSurvey} apiBase={finalApiBase} />}
          {showNdvi && <NdviCesiumLayer viewer={viewerRef.current} latestSurvey={latestSurvey} apiBase={finalApiBase} />}
          {showVectors && <VectorCesium viewer={viewerRef.current} latestSurvey={latestSurvey} apiBase={finalApiBase} />}
          {showImages && <ImageCesium viewer={viewerRef.current} latestSurvey={latestSurvey} apiBase={finalApiBase} />}
          
          <CrossSectionCesium 
            viewer={viewerRef.current}
            isActive={isCrossSectionMode}
            onClose={() => setIsCrossSectionMode(false)}
            onStatusChange={(msg) => setStatusText(msg)}
            layerDependency={[show3D, showOrtho, showNdvi, showVectors, showImages, showLidar]}
          />
        </>
      )}
    </div>
  );
}

// --- STYLES ---
const mapControlStyle = {
    position: "absolute",
    zIndex: 2001,
    background: "rgba(255,255,255,0.95)",
    padding: "6px 12px",
    borderRadius: 6,
    boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#333",
    fontFamily: "sans-serif"
};

const textButtonStyle = {
    border: "none",
    background: "transparent",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    padding: "4px 4px",
    color: "#0f172a",
    display: 'flex',
    alignItems: 'center',
    fontFamily: "inherit"
};

const dividerStyle = {
    width: 1,
    height: 16,
    background: "#ccc",
    margin: "0 4px"
};

const layerRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    cursor: 'pointer'
};

// --- COMPASS STYLES ---
const compassContainerStyle = {
    position: 'absolute',
    bottom: 30,
    right: 20, 
    zIndex: 2005, // Higher than everything
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pointerEvents: 'none', 
};

const compassInnerStyle = {
    cursor: 'pointer',
    pointerEvents: 'auto',
    width: 45,
    height: 45,
    // Note: The rotation is applied via inline style ref in the component
    transition: 'transform 0.05s linear' // Very fast transition for smoothness
};

const compassHelpTextStyle = {
    marginTop: 8, 
    fontSize: 10, 
    color: '#fff', 
    textShadow: '0 1px 3px rgba(0,0,0,0.9)', 
    textAlign: 'center', 
    opacity: 0.9,
    fontFamily: 'sans-serif'
};